import React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useAuth } from '../../../../contexts/AuthContext';
import { Unauthorized } from '../../../../components/Unauthorized';
import useChart from '../../../../hooks/useChart';
import { Chart } from '../../../../types/Chart/Chart';
import { PhotoCamera } from '../../../../components/PhotoCamera';
import { FileTypes } from '../../../../types/Files/FileTypes';
import { PhotoDimensions } from '../../../../types/Camera/PhotoDimensions';
import { Button } from '../../../../components/buttons/Button';

import { Heading } from '../../../../components/Heading';

export const Route = createFileRoute('/_authenticated/$patientId/capture/id')({
  component: IdFront,
});

function IdFront() {
  const [takingPhoto, setTakingPhoto] = React.useState({
    idFront: false,
    idBack: false,
    insuranceFront: false,
    insuranceBack: false,
    medicareFront: false,
    medicareBack: false,
    portrait: false,
    testDocument: false,
  });
  const { patientId } = Route.useParams<{ patientId: string }>();
  const { currentUser } = useAuth();
  const chartQuery = useChart(patientId);
  const chart = chartQuery.data as Chart;
  const [photoOneWorking, setPhotoOneWorking] = React.useState(false);
  const [photoTwoWorking, setPhotoTwoWorking] = React.useState(false);

  React.useEffect(() => {
    if (chart && chart.PHOTO_ID) {
      if (chart?.PHOTO_ID?.photoIdentificationFrontURI && chart?.PHOTO_ID?.photoIdentificationBackURI) {
        alert(
          'You have successfully uploaded your ID. You can now return to your other device or retake a photo if you wish.',
        );
      }
    }
  }, [chart]);

  // RESTORE NORMAL NAVIGATION
  window.localStorage.removeItem('requestedURL');

  if (chartQuery.isLoading || !chart) {
    return <div>Loading patient information...</div>;
  }
  if (!currentUser) {
    return <div>Loading current user</div>;
  }
  if (currentUser?.uid !== patientId) {
    return <Unauthorized />;
  }
  if (currentUser?.uid === patientId) {
    return (
      <>
        <div id="photo-upload-page" className="flex flex-col items-center">
          <Heading id="photo-upload-page-header" textContent="Capture Photo ID" />
          <div className="my-4">
            <ul className="my-4 text-xl font-bold">
              Tips for Taking a Good Photo
              <li className="text-xl font-normal">
                - Position the card to fill as much of the photo box as possible without cropping.
              </li>
              <li className="text-xl font-normal">- Wait for your camera to completely auto-focus</li>
              <li className="text-xl font-normal">
                - Position your phone to avoid as much glare as possible when taking a photo.
              </li>
            </ul>
            <p className="text-2xl font-bold">ID FRONT</p>
            <div id="front-id-container" className="flex flex-col items-center">
              {chart?.PHOTO_ID?.photoIdentificationFrontURI && takingPhoto.idFront === false ? (
                <div className="flex flex-row items-center justify-center">
                  <img
                    src={chart?.PHOTO_ID?.photoIdentificationFrontURI}
                    style={{ width: `${PhotoDimensions.desktopWidth}%` }}
                    alt="ID Front"
                  />
                  <div className="ml-4">
                    <Button
                      id="retake-gov-id-front"
                      type="button"
                      textContent="Retake Photo"
                      onClick={() => {
                        setTakingPhoto({ ...takingPhoto, idFront: true });
                      }}
                    />
                  </div>
                </div>
              ) : (
                takingPhoto.idFront === false && (
                  <Button
                    id="take-gov-id-front"
                    type="button"
                    textContent="Take Photo"
                    onClick={() => {
                      setTakingPhoto({ ...takingPhoto, idFront: true });
                    }}
                  />
                )
              )}
              {takingPhoto.idFront && (
                <PhotoCamera
                  type={FileTypes.idFront}
                  docToUpdate="PHOTO_ID"
                  style={'card'}
                  bucket={'patient'}
                  path={`user/${patientId}/images/photoIdentificationFront`}
                  patientId={patientId}
                  hideCamera={() => {
                    setPhotoOneWorking(false);
                    setTakingPhoto({ ...takingPhoto, idFront: false });
                  }}
                  working={photoOneWorking}
                  setWorking={setPhotoOneWorking}
                />
              )}
            </div>
          </div>
          <div className="my-4">
            <p className="text-2xl font-bold">ID BACK</p>
            <div id="back-id-container" className="flex flex-col items-center">
              {chart?.PHOTO_ID?.photoIdentificationBackURI && takingPhoto.idBack === false ? (
                <div className="flex flex-row items-center justify-center">
                  <img
                    src={chart?.PHOTO_ID?.photoIdentificationBackURI}
                    style={{ width: `${PhotoDimensions.desktopWidth}%` }}
                    alt="ID Back"
                  />
                  <div className="ml-4">
                    <Button
                      id="retake-gov-id-back"
                      type="button"
                      textContent="Retake Photo"
                      onClick={() => {
                        setTakingPhoto({ ...takingPhoto, idBack: true });
                      }}
                    />
                  </div>
                </div>
              ) : (
                takingPhoto.idBack === false && (
                  <Button
                    id="take-gov-id-back"
                    type="button"
                    textContent="Take Photo"
                    onClick={() => {
                      setTakingPhoto({ ...takingPhoto, idBack: true });
                    }}
                  />
                )
              )}
              {takingPhoto.idBack && (
                <PhotoCamera
                  type={FileTypes.idBack}
                  docToUpdate="PHOTO_ID"
                  style={'card'}
                  bucket={'patient'}
                  path={`user/${patientId}/images/photoIdentificationBack`}
                  patientId={patientId}
                  hideCamera={() => {
                    setPhotoTwoWorking(false);
                    setTakingPhoto({ ...takingPhoto, idBack: false });
                  }}
                  working={photoTwoWorking}
                  setWorking={setPhotoTwoWorking}
                />
              )}
            </div>
          </div>
        </div>
      </>
    );
  }

  return <div>Loading...</div>;
}
